import {cloneDeep} from 'lodash';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {FmlxIcon, FmlxLabel} from 'fmlx-common-ui';
import {useManageQuestionContext} from '../../../lib/contexLib';
import {FMLX_COMPONENT} from '../../../const/FmlxComponentEnum';
import {ACTIVE_TAB, ALERT_VARIANT} from '../../../const/dataEnum';
import {setAlert} from '../../../stores/actions/alertModalStateAction';
import Question from './Question';
import NewQuestion from './NewQuestion';
import AddQuestions from './AddQuestions';
import DepartmentDropdown from '../../../components/CommonDropdown/DepartmentDropdown';
import CategoryDescription from './CategoryDescription';
import {manageQuestionBusiness} from '../../../stores/business/manageQuestionBusiness';
import skillApi from '../../../api/rest/skillApi';
import genericReviewQuestionApi from '../../../api/rest/genericReviewQuestionApi';
import { selectPeriodeAndAuthRole } from '../../../stores/selectors/appStateSelector';

const NavContent = ({isGeneral, fetchTabs}) => {
  const dispatch = useDispatch();
  const {
    selectedDepartment,
    selectedPractice,
    activeTab,
    activeTabQuestion,
    setActiveTabQuestion,
    handleTabsData,
    setAllTabs,
    setSelectedDepartment,
    departments,
  } = useManageQuestionContext();

  const {currentPeriodId} = useSelector(selectPeriodeAndAuthRole);
  const [newQuestionFlag, setNewQuestionFlag] = useState(false);

  const [editId, setEditId] = useState(-9);
  useEffect(() => {
    setNewQuestionFlag(false);
    setEditId(-9);
  }, [activeTab]);

  const handleAddQuestion = useCallback(() => {
    let tempQuestions = activeTabQuestion !== null ? cloneDeep(activeTabQuestion) : [];
    const newOrderNumber = tempQuestions.length + 1;
    const newQuestion = isGeneral
      ? {
          orderNumber: newOrderNumber,
          question: '',
          forSelf: 0,
          forPeer: 0,
          forManager: 0,
          departmentID: selectedDepartment?.id,
          department: null,
          questionType: 'essay',
          weight: 1,
          description: '',
          required: false,
          reviewPeriodID: currentPeriodId,
          isNew: true,
          minimumofWords: 50,
        }
      : {
          name: '',
          description: '',
          orderNumber: newOrderNumber,
          skillCategoryID: activeTab?.id,
          forSelf: 0,
          forPeer: 0,
          forManager: 0,
          departmentID: selectedDepartment?.id,
          practiceID: selectedPractice?.id,
          weight: 1,
          skillType: 'essay',
          practice: null,
          reviewPeriodID: currentPeriodId,
          reviewPeriod: null,
          isNew: true,
          minimumofWords: 50,
        };

    tempQuestions.push(newQuestion);

    const newQuestionIndex = tempQuestions.length - 1;
    setNewQuestionFlag(true);
    setEditId(newQuestionIndex);
    setActiveTabQuestion(tempQuestions);
  }, [
    activeTabQuestion,
    isGeneral,
    selectedDepartment?.id,
    activeTab?.id,
    selectedPractice?.id,
    currentPeriodId,
    setActiveTabQuestion,
    setNewQuestionFlag,
    setEditId,
  ]);

  const handleSaveNewQuestion = async (newQuestion) => {
    const newQuestions = activeTabQuestion.map((question) => {
      if (question.orderNumber === newQuestion.orderNumber) {
        return newQuestion;
      }
      return question;
    });
    await dispatch(manageQuestionBusiness.updateQuestions(newQuestions, isGeneral));
    await handleTabsData(selectedDepartment?.id);
    setEditId(-9);
    setNewQuestionFlag(false);
  };

  const handleRemoveQuestion = async (question) => {
    /** call delete question api if question is not new */
    if (!question?.isNew) {
      if (isGeneral) await deleteGenericQuestion(question?.id);
      else await deleteSkill(question?.id);
    } else {
      /** remove question from state if question is new */
      const newQuestions = manageQuestionBusiness.removeNewQuestionFromState(activeTabQuestion, question, isGeneral);
      setActiveTabQuestion(newQuestions);
    }

    if (newQuestionFlag) {
      setNewQuestionFlag(false);
    }
    setEditId(-9);
  };

  const handleOnCloseQuestion = () => {
    handleTabsData(selectedDepartment?.id);
    setEditId(-9);
    setNewQuestionFlag(false);
  };

  const deleteSkill = async (index) => {
    if (newQuestionFlag) {
      activeTabQuestion.shift();
      let tempQuestions = cloneDeep(activeTabQuestion);

      for (let i = 1; i <= tempQuestions.length; i++) {
        tempQuestions[i - 1].orderNumber = i;
      }
      setActiveTabQuestion(tempQuestions ? cloneDeep(tempQuestions) : []);
    } else {
      try {
        const payload = {Id: index, departmentID : selectedDepartment.id, practiceID: selectedPractice.id, reviewPeriodId: currentPeriodId, skillCategoryID: activeTab.id}
        const response = await skillApi.deleteSkill(payload);
        const {message, responseCode} = response;
        if (responseCode === 0) {
          let tempQuestions = activeTabQuestion.filter((question) => index !== question.id);
          setActiveTabQuestion(tempQuestions ? cloneDeep(tempQuestions) : []);
          dispatch(setAlert({show: true, variant: ALERT_VARIANT.SUCCESS, message: message}));
          setAllTabs()
        }
      } catch (error) {
        dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: error.data}));
      }
    }
  };

  const deleteGenericQuestion = async (index) => {
    if (newQuestionFlag) {
      activeTabQuestion.shift();
      let tempQuestions = cloneDeep(activeTabQuestion);

      for (let i = 1; i <= tempQuestions.length; i++) {
        tempQuestions[i - 1].orderNumber = i;
      }
      setActiveTabQuestion(tempQuestions ? cloneDeep(tempQuestions) : []);
    } else {
      try {
        const response = await genericReviewQuestionApi.deleteGenericQuestion({Id: index});
        const {message, responseCode} = response;
        if (responseCode === 0) {
          let tempQuestions = activeTabQuestion.filter((question) => index !== question.id);
          setActiveTabQuestion(tempQuestions ? cloneDeep(tempQuestions) : []);
          dispatch(setAlert({show: true, variant: ALERT_VARIANT.SUCCESS, message: message}));
          handleTabsData(selectedDepartment?.id);
        }
      } catch (error) {
        dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: error.data}));
      }
    }
  };

  const handleQuestionClick = (index) => {
    setEditId(index);
  };

  const handleOnChangeQuestionPosition = (motion, index) => {
    switch (motion) {
      case 'up':
        if (index === 0) return;
        swap(index, index - 1);
        break;
      case 'down':
        if (index === activeTabQuestion.length - 1) return;
        swap(index, index + 1);
        break;
      default:
        break;
    }
  };

  const onEditQuestion = async (question) => {
    let tempQuestions = cloneDeep(question);
    for (let i = 1; i <= tempQuestions.length; i++) {
      tempQuestions[i - 1].orderNumber = i;
    }
    if (isGeneral) await editQuestionApi(tempQuestions);
    else await editSkillApi(tempQuestions);
    await handleTabsData(selectedDepartment?.id);
    if (newQuestionFlag) {
      setNewQuestionFlag(false);
    }
  };

  const swap = async (current, target) => {
    let tempQuestions = cloneDeep(activeTabQuestion);
    const tempQuest = tempQuestions.splice(current, 1);
    tempQuestions.splice(target, 0, tempQuest[0]);
    if (current === editId) {
      setEditId(target);
    } else if (target === editId) {
      setEditId(current);
    }

    for (let i = 1; i <= tempQuestions.length; i++) {
      tempQuestions[i - 1].orderNumber = i;
    }
    try {
      const response = isGeneral
        ? await genericReviewQuestionApi.updateBatch(tempQuestions)
        : await skillApi.updateBatch(tempQuestions);

      const {message, responseCode} = response;
      if (responseCode === 0) {
        // let sortedQuestions = questions.sort((a, b) => a.orderNumber - b.orderNumber);
        // let ques = { ...question, question: questions };
        // let tempQuestions = allQuestions.map((quest) => (quest.categoryId === activeTab.id ? ques : quest));
        // setAllQuestions(tempQuestions);
        handleTabsData(selectedDepartment?.id);
        setEditId(-9);
        setNewQuestionFlag(false);
      }
    } catch (error) {
      dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: error.data}));
    }
  };

  const editSkillApi = async (body) => {
    try {
      const response = await skillApi.modify(body);
      const {message, responseCode} = response;
      if (responseCode === 0) {
        dispatch(setAlert({show: true, variant: ALERT_VARIANT.SUCCESS, message: message}));
        setEditId(-9);
      }
      return response;
    } catch (error) {
      dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: error.message}));
    }
  };

  const editQuestionApi = async (body) => {
    try {
      const response = await genericReviewQuestionApi.update(body);
      const {message, responseCode} = response;
      if (responseCode === 0) {
        dispatch(setAlert({show: true, variant: ALERT_VARIANT.SUCCESS, message: message}));
        setEditId(-9);
      }
      return response;
    } catch (error) {
      dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: error.message}));
    }
  };

  const handleOnCopyQuestion = (index) => {
    let copyData = {...activeTabQuestion[index]};
    if (isGeneral) {
      copyData.question = `Copy of ${copyData.question}`;
    } else {
      copyData.name = `Copy of ${copyData.name}`;
    }
    delete copyData.id;
    copyData.isNew = true;
    copyData.orderNumber = copyData.orderNumber + 1;
    const tempQuestions = activeTabQuestion.map((ques) => ({
      ...ques,
      // orderNumber: index + 1,
      orderNumber: ques.orderNumber > copyData.orderNumber ? ques.orderNumber + 1 : ques.orderNumber,
    }));

    tempQuestions.splice(index + 1, 0, copyData);
    for (let i = 1; i <= tempQuestions.length; i++) {
      tempQuestions[i - 1].orderNumber = i;
    }

    setActiveTabQuestion(tempQuestions ? tempQuestions : []);
    setEditId(index + 1);
    setNewQuestionFlag(true);
  };

  const memoizedAddQuestionsComponent = useMemo(
    () => <AddQuestions handleAddQuestion={handleAddQuestion} />,
    [handleAddQuestion]
  );

  const onDepartmentChangeHandler = (department) => {
    if (department !== null) {
      setSelectedDepartment(department);
      handleTabsData(department?.id);
    }
  };

  return (
    <div className="manageQuestionPage-content-NavContent">
      {activeTab ? (
        <div key={`${activeTab.category}`}>
          <div className="questions_header" key={0}>
            <CategoryDescription isGeneral={isGeneral} fetchTabs={fetchTabs} />
            {!newQuestionFlag &&
              selectedDepartment !== null &&
              (isGeneral || (selectedPractice !== null && currentPeriodId !== null)) &&
              memoizedAddQuestionsComponent}
          </div>
          {isGeneral ? (
            <div>
              <FmlxLabel label="Select Department" required={false} />
              <DepartmentDropdown
                callbackChangeDepartment={onDepartmentChangeHandler}
                departmentValue={selectedDepartment}
              />
            </div>
          ) : (
            <></>
          )}
          {activeTabQuestion?.map((question, index) => {
            return (
              <React.Fragment key={index + 1}>
                {index === editId ? (
                  <NewQuestion
                    index={index}
                    departments={departments}
                    question={question}
                    onEditQuestion={onEditQuestion}
                    removeQuestion={handleRemoveQuestion}
                    handleSaveNewQuestion={handleSaveNewQuestion}
                    changeQuestionPosition={handleOnChangeQuestionPosition}
                    onCloseQuestion={handleOnCloseQuestion}
                    skill={activeTab?.category === ACTIVE_TAB.GENERAL ? false : true}
                  />
                ) : (
                  <Question
                    index={index}
                    departments={departments}
                    question={question}
                    isActionDisabled={editId !== -9}
                    copyQuestion={handleOnCopyQuestion}
                    clickHandler={handleQuestionClick}
                    changeQuestionPosition={handleOnChangeQuestionPosition}
                    onCloseQuestion={handleOnCloseQuestion}
                    skill={activeTab?.category === ACTIVE_TAB.GENERAL ? false : true}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      ) : (
        <div className="noData">
          <FmlxIcon name={'UserIncoming'} size={FMLX_COMPONENT.SIZE.XL} />
          <p>
            {currentPeriodId && selectedDepartment && selectedPractice
              ? 'no data found'
              : 'Select Values for Period, Department and Practice'}
          </p>
        </div>
      )}
    </div>
  );
};

export default NavContent;
